import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, User, Mail, Phone, MapPin, Clock, FileText, Users, CheckCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PeaceArchitectApplication() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    age: "",
    neighborhood: "",
    why_interested: "",
    leadership_experience: "",
    community_involvement: "",
    availability: "",
    references: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone || !formData.why_interested) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null
      };
      
      await axios.post(`${API}/peace-architect/apply`, submitData);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center px-4">
        <div className="max-w-lg w-full text-center">
          <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-3xl p-10">
            <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">Application Submitted!</h1>
            <p className="text-slate-300 mb-8">
              Thank you for applying to become a Peace Architect. We will review your application and contact you soon.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate("/peace-pilot")}
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              >
                Back to Peace Pilot
              </Button>
              <Button 
                onClick={() => navigate("/")}
                variant="outline"
                className="border-amber-500 text-amber-400 hover:bg-amber-500/10"
              >
                Go to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="peace-architect-application-page">
      {/* Hero Section */}
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d2040] via-[#0a1628] to-[#0a1628]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/peace-pilot")}
            className="text-slate-400 hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Peace Pilot
          </Button>
          
          <div className="text-center">
            <div className="flex justify-center gap-1 mb-4">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-6 h-6 text-amber-400 fill-amber-400" />
              ))}
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-['Outfit'] text-white mb-4">
              Peace Architect Application
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-4">
              Join the 5-Star Peace Pilot as a Youth Peace Architect. Lead the way in creating safer spaces in Milwaukee's most iconic locations.
            </p>
            <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/30 rounded-full px-6 py-2">
              <span className="text-amber-400 font-semibold">Now Recruiting:</span>
              <span className="text-white">2 Female Teens & 3 Male Teens</span>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-12 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-10">
            {/* Personal Information */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-amber-400" />
                </div>
                <h2 className="text-xl font-bold text-white">Personal Information</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">First Name *</Label>
                  <Input
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="Your first name"
                    className="mt-1 bg-slate-800/50 border-slate-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Last Name *</Label>
                  <Input
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Your last name"
                    className="mt-1 bg-slate-800/50 border-slate-600 text-white"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-slate-300">Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="pl-10 bg-slate-800/50 border-slate-600 text-white"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Phone *</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(414) 555-0123"
                      className="pl-10 bg-slate-800/50 border-slate-600 text-white"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-slate-300">Age</Label>
                  <Input
                    name="age"
                    type="number"
                    value={formData.age}
                    onChange={handleChange}
                    placeholder="Your age"
                    className="mt-1 bg-slate-800/50 border-slate-600 text-white"
                    min="14"
                    max="30"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Neighborhood</Label>
                  <div className="relative mt-1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      name="neighborhood"
                      value={formData.neighborhood}
                      onChange={handleChange}
                      placeholder="Your Milwaukee neighborhood"
                      className="pl-10 bg-slate-800/50 border-slate-600 text-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Why Are You Interested */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-400" />
                </div>
                <h2 className="text-xl font-bold text-white">Your Story</h2>
              </div>

              <div>
                <Label className="text-slate-300">Why do you want to be a Peace Architect? *</Label>
                <Textarea
                  name="why_interested"
                  value={formData.why_interested}
                  onChange={handleChange}
                  placeholder="Tell us why you want to join the 5-Star Peace Pilot and what motivates you to make a difference in your community..."
                  className="mt-1 bg-slate-800/50 border-slate-600 text-white min-h-[120px]"
                  required
                />
              </div>

              <div className="mt-4">
                <Label className="text-slate-300">Leadership Experience</Label>
                <Textarea
                  name="leadership_experience"
                  value={formData.leadership_experience}
                  onChange={handleChange}
                  placeholder="Describe any leadership roles you've held in school, community groups, sports, or other organizations..."
                  className="mt-1 bg-slate-800/50 border-slate-600 text-white min-h-[100px]"
                />
              </div>

              <div className="mt-4">
                <Label className="text-slate-300">Community Involvement</Label>
                <Textarea
                  name="community_involvement"
                  value={formData.community_involvement}
                  onChange={handleChange}
                  placeholder="How are you currently involved in your community? What activities, groups, or causes do you participate in?"
                  className="mt-1 bg-slate-800/50 border-slate-600 text-white min-h-[100px]"
                />
              </div>
            </div>

            {/* Availability & References */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <h2 className="text-xl font-bold text-white">Availability & References</h2>
              </div>

              <div>
                <Label className="text-slate-300">Availability</Label>
                <Textarea
                  name="availability"
                  value={formData.availability}
                  onChange={handleChange}
                  placeholder="What days and times are you typically available? (e.g., Weekends, Evenings after 5pm, etc.)"
                  className="mt-1 bg-slate-800/50 border-slate-600 text-white"
                  rows={3}
                />
              </div>

              <div className="mt-4">
                <Label className="text-slate-300">References</Label>
                <Textarea
                  name="references"
                  value={formData.references}
                  onChange={handleChange}
                  placeholder="Please provide 1-2 references (name, relationship, phone/email)"
                  className="mt-1 bg-slate-800/50 border-slate-600 text-white"
                  rows={3}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4 border-t border-white/10">
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold py-6 text-lg"
                disabled={submitting}
              >
                {submitting ? (
                  "Submitting..."
                ) : (
                  <>
                    <Star className="w-5 h-5 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
              <p className="text-center text-slate-500 text-sm mt-4">
                By submitting this application, you agree to be contacted by Peace for Change Alliance Inc.
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
